import React, { useState } from "react";
import { Headline } from "../../atoms/headline";
import { Button } from "../../atoms/button";
import { useSelector, useDispatch } from "react-redux";
import "./reset-password.module.css";
import {
  getErrorStyle,
  genericError,
  triggerResetPassword,
  triggerLogin,
  triggerSSO,
  triggerUpdateWithOtp,
  inputFieldError,
  retrieveNumbers,
} from "../../util";
import { ErrorMessage } from "../../atoms/error-message";
import {
  CREATE_NEW_PASSWORD,
  ENTER_OTP,
  GO_BACK_BUTTON,
  QUILL_EMPTY_STRING,
  RESET_PASSWORD_SUBTITLE,
  UPDATE_FORGOT_PASSWORD,
  UPDATE_SINGLE_SIGN_ON,
  RESET_PASSWORD_TITLE,
  VERIFY_SIGN_IN_BUTTON,
  PASSWORD_RESET_SUCCESS,
} from "../../../../../constants";
import get from "lodash/get";
import { ResendOtp } from "../../atoms/resend-otp";
import { SvgHandler } from "../../../../svg-handler/svg-handler";
import visibilityIcon from "../../../../../../assets/icons/visibility.svg";
import { checkIfSingleSignOn } from "../../sso-utils";
import visibilityOffIcon from "../../../../../../assets/icons/visibility-off.svg";
import { SuccessMessage } from "../../atoms/success-message";

export const ResetPasswordScreen = ({ mode, isUserLoginPage = false }) => {
  const { forgotPassword = {}, singleSignOn = {} } = useSelector((state) => get(state, ["login"], {}));
  const { currentPath = "", currentHostUrl = "" } = useSelector((state) => get(state, ["qt"], {}));
  const { inputData = "", password: userPassword = "" } = get(forgotPassword, ["userData"], {});
  const { inputData: ssoInputData, password: ssoPassword = "" } = get(singleSignOn, ["userData"], {});
  const localStorageEmail = global?.localStorage?.getItem("userEmail");
  const emailData = inputData || localStorageEmail;
  const email = !isUserLoginPage ? emailData : ssoInputData;
  const [otp, setOtp] = useState("");
  const [password, setPassword] = useState(!isUserLoginPage ? userPassword : ssoPassword);
  const [response, setResponse] = useState(null);
  const [api, setApi] = useState(null);
  const [displaySuccess, setSuccess] = useState(false);
  const [hidePassword, setHidePassword] = useState(true);
  const dispatch = useDispatch();
  const { general = {}, login = {} } = useSelector((state) => get(state, ["qt", "config", "pagebuilder-config"], {}));
  const {
    pageTitle,
    pageSubTitle,
    enterOtpLabel,
    resendOtpInLabel,
    resendOtpLabel,
    newPasswordLabel,
    button,
    goBackButton,
  } = get(login, ["reset-password", "rows", 0, "config"], {});
  const { passwordResetSuccess = "" } = get(login, ["pageSetting"], {});
  const { sso = {} } = general;

  const triggerVerify = async () => {
    const triggerSuccess = () => {
      if (isUserLoginPage && checkIfSingleSignOn(sso) && window?.location) {
        const ssoProps = { sso: sso, currentHostUrl: currentHostUrl, currentPath: currentPath };
        triggerSSO(ssoProps);
      }
      global?.localStorage?.removeItem("userEmail");
      setSuccess(true);
    };

    const resetPasswordProps = { email: email, otp: otp, password: password };
    const resetResponse = await triggerResetPassword(resetPasswordProps);
    setResponse(resetResponse);
    setApi("reset-password");
    if (resetResponse === 200) {
      const loginProps = { data: email, credential: password, type: "password" };
      const { responseStatus: loginResponseStatus, qtAuthCookie, verificationStatus } = await triggerLogin(loginProps);
      setResponse(loginResponseStatus);
      setApi("login-email");
      if (loginResponseStatus === 200) {
        if (!verificationStatus) {
          const updateOtpProps = { otp: otp, email: email, authCookie: qtAuthCookie };
          const updateWithOtpResponse = await triggerUpdateWithOtp(updateOtpProps);
          setResponse(updateWithOtpResponse);
          setApi("update-with-otp");
          if (updateWithOtpResponse === 200) {
            triggerSuccess();
          }
        } else {
          triggerSuccess();
        }
      }
    }
  };

  const triggerGoBack = () => {
    dispatch({
      type: !isUserLoginPage ? UPDATE_FORGOT_PASSWORD : UPDATE_SINGLE_SIGN_ON,
      payload: {
        currentScreen: "forgotPassword",
        userData: { inputData: email, password: password },
      },
    });
  };
  const wrapperStyle = displaySuccess ? `auth--wrapper ${mode} success-wrapper` : `auth--wrapper ${mode}`;
  return (
    <div className={wrapperStyle}>
      <Headline content={pageTitle && pageTitle !== QUILL_EMPTY_STRING ? pageTitle : RESET_PASSWORD_TITLE} />
      <div styleName="description">{pageSubTitle || RESET_PASSWORD_SUBTITLE}</div>
      <div styleName="input-otp" className={getErrorStyle(response, api, mode)}>
        <label for="email">{enterOtpLabel || ENTER_OTP}</label>
        <input
          inputmode="numeric"
          pattern="[0-9]*"
          id="otp"
          name="otp"
          value={otp}
          onChange={(event) => {
            setResponse(null);
            setApi(null);
            setOtp(retrieveNumbers(event.target.value));
          }}
          required
        />
        <ResendOtp
          resendOtpLabel={resendOtpLabel}
          resendOtpInLabel={resendOtpInLabel}
          setResponse={setResponse}
          setApi={setApi}
          email={email}
        />
        {inputFieldError(response, api) ? <ErrorMessage response={response} api={api} mode={mode} /> : <></>}
      </div>
      <div styleName="input-password">
        <label for="email">{newPasswordLabel || CREATE_NEW_PASSWORD}</label>
        <input
          type={hidePassword ? "password" : "text"}
          id="password"
          name="password"
          value={password}
          onChange={(event) => {
            setResponse(null);
            setApi(null);
            setPassword(event.target.value);
          }}
          required
        />
        <div styleName="eye-wrapper" onClick={() => setHidePassword(!hidePassword)}>
          <SvgHandler
            xlinkHref={hidePassword ? visibilityIcon : visibilityOffIcon}
            width="24"
            height="24"
            viewBox="0 0 24 24"
            invertColor={mode === "dark"}
          />
        </div>
      </div>
      <Button
        text={button || VERIFY_SIGN_IN_BUTTON}
        clickHandler={() => triggerVerify()}
        disable={!otp || !password}
        enableEnterClick={true}
      />
      {genericError(response, api) ? <ErrorMessage response={response} api={api} mode={mode} /> : <></>}
      <div styleName="custom-button" className="auth--text-color">
        <Button text={goBackButton || GO_BACK_BUTTON} clickHandler={() => triggerGoBack()} />
      </div>
      {displaySuccess ? (
        <SuccessMessage
          message={passwordResetSuccess || PASSWORD_RESET_SUCCESS}
          mode={mode}
          isUserLoginPage={isUserLoginPage}
        />
      ) : (
        <></>
      )}
    </div>
  );
};
